import React, { useState } from 'react';
import { Search, MessageCircle, Phone, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import translations from '../contexts/translations';

const Help = ({ language = 'en' }) => {
  const t = translations[language];
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const faqs = [
    {
      question: t.faq1Question,
      answer: t.faq1Answer
    },
    {
      question: t.faq2Question,
      answer: t.faq2Answer
    },
    {
      question: t.faq3Question,
      answer: t.faq3Answer
    },
    {
      question: t.faq4Question,
      answer: t.faq4Answer
    }
  ];

  const filteredFAQs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full transform translate-x-48 -translate-y-48 blur-3xl bg-orange-200/30"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full transform -translate-x-40 translate-y-40 blur-3xl bg-amber-200/20"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full transform -translate-x-32 -translate-y-32 blur-3xl bg-orange-100/40"></div>
      
      <div className="relative z-10 py-24">
        <div className="container mx-auto px-4" style={{ marginTop: '3rem' }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold text-orange-600 mb-6">{t.helpPageTitle}</h1>
            <p className="text-xl text-amber-900/80 max-w-2xl mx-auto">
              {t.helpPageSubtitle}
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-2xl mx-auto mb-12"
          >
            <div className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-orange-400 w-6 h-6" />
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 border border-amber-200 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-300 bg-white/80 text-amber-900 placeholder-amber-600/60 text-lg"
                />
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* FAQ Section */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-8"
              >
                <h2 className="text-3xl font-semibold mb-8 text-orange-600 text-center">{t.faqTitle}</h2>
                <div className="space-y-4">
                  {filteredFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="border border-amber-200 rounded-2xl overflow-hidden"
                    >
                      <button
                        onClick={() => toggleFAQ(index)}
                        className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-orange-50 focus:outline-none focus:bg-orange-50 transition-colors duration-200"
                      >
                        <span className="font-medium text-amber-900 text-lg">{faq.question}</span>
                        {expandedFAQ === index ? (
                          <ChevronUp className="w-6 h-6 text-orange-500" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-orange-500" />
                        )}
                      </button>
                      {expandedFAQ === index && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-6 pb-5 bg-orange-50/50"
                        >
                          <p className="text-amber-900/80 leading-relaxed">{faq.answer}</p>
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Contact Options */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-6"
              >
                <div className="flex items-center mb-4">
                  <MessageCircle className="w-6 h-6 text-orange-600 mr-3" />
                  <h3 className="text-lg font-semibold text-amber-900">{t.liveChat}</h3>
                </div>
                <p className="text-amber-900/70 mb-4">{t.liveChatDesc}</p>
                <button className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 px-4 rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-200 shadow-lg">
                  {t.startChat}
                </button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-6"
              >
                <div className="flex items-center mb-4">
                  <Phone className="w-6 h-6 text-orange-600 mr-3" />
                  <h3 className="text-lg font-semibold text-amber-900">{t.phoneSupport}</h3>
                </div>
                <p className="text-amber-900/70 mb-2">{t.phoneSupportDesc}</p>
                <p className="font-semibold text-orange-600">+212 651-913292</p>
                <p className="text-sm text-amber-700/60">{t.businessHours}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-6"
              >
                <div className="flex items-center mb-4">
                  <Mail className="w-6 h-6 text-orange-600 mr-3" />
                  <h3 className="text-lg font-semibold text-amber-900">{t.emailSupport}</h3>
                </div>
                <p className="text-amber-900/70 mb-2">{t.emailSupportDesc}</p>
                <p className="font-semibold text-orange-600">Contact@palaistouristique.ma</p>
                <p className="text-sm text-amber-700/60">{t.responseTime}</p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;